import { alpha, type Components, type Theme } from '@mui/material/styles';
import { uiBorderColor } from './stylePacks';

/**
 * Style-rule registry — the T2 tier (theming-dls-engine.md §11A.3, §21).
 *
 * Consumes `l_portal_theme_base.style_rules_json`, served as `PPThemeStyleRules`, so a base can
 * change component styling **without a code deploy** — bounded to a reviewed vocabulary.
 *
 * WHY A REGISTRY AND NOT RAW OVERRIDES
 * A raw `components` blob from the database could set `outline: none`, 8px body text, a 12px
 * button, or physical `left`/`right` that breaks Arabic. Every key here is explicitly
 * implemented, so the reachable surface is exactly what was reviewed. There is no `outline`
 * entry, interactive heights have floors, and every value is clamped or dropped rather than
 * passed through.
 *
 * WHY VIRTUAL PATHS
 * The original design sketched flat `Component.slot.property` keys. That cannot express what the
 * DLS actually needs: a pill tab requires `&.Mui-selected`, and an accent-border alert requires
 * per-severity selectors — pseudo-classes, not slots. So a key is a *virtual* path and each entry
 * carries its own `apply()`. `MuiTab.selected.backgroundColor` resolves to
 * `MuiTab.styleOverrides.root['&.Mui-selected']` in code, which means an author can reach the
 * selected state but still cannot reach anything unregistered.
 *
 * TWO ENTRY KINDS
 *   `variantStyle` presets — one key, several curated looks (pill vs underline tabs, accent-border
 *   vs filled alerts). This is what an admin actually wants: a dropdown, not twelve hex fields.
 *   Fine-grain properties — radius, weight, uppercase, sizes, for tweaks on top.
 *
 * FAIL-SAFE BY DESIGN
 * Unknown key → ignored. Out-of-range length → clamped. Invalid enum → ignored. That is why no
 * C# mirror of this registry exists: duplicating it server-side would drift, and apply-time is
 * already safe. The server validates JSON shape only; the admin editor validates semantics.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Spec model
// ─────────────────────────────────────────────────────────────────────────────

export type RuleGroup =
  | 'Tabs' | 'Alerts' | 'Tables' | 'Fields'
  | 'Buttons' | 'Cards' | 'Chips' | 'Surfaces' | 'Disclosure' | 'Lists';

export type RuleSpec =
  | { kind: 'enum'; group: RuleGroup; label: string; values: readonly string[]; help?: string }
  | { kind: 'length'; group: RuleGroup; label: string; min: number; max: number; help?: string }
  | { kind: 'number'; group: RuleGroup; label: string; values: readonly number[]; help?: string };

/** A fragment of MUI `components`, deep-merged with the others. */
type Fragment = Components<Theme>;

interface RuleEntry {
  spec: RuleSpec;
  apply: (value: string, theme: Theme) => Fragment;
}

/** `elevationN` → the theme's shadow scale; `none` → no shadow. */
const shadow = (theme: Theme, v: string): string | undefined => {
  if (v === 'none') return 'none';
  const n = Number(v.replace('elevation', ''));
  return Number.isFinite(n) && n >= 0 && n <= 24 ? theme.shadows[n] : undefined;
};

const SHADOW_VALUES = ['none', 'elevation1', 'elevation2', 'elevation4', 'elevation8'] as const;
const TRANSFORM_VALUES = ['none', 'uppercase', 'capitalize'] as const;
const WEIGHT_VALUES = [400, 500, 600, 700, 800] as const;
const TRACKING_VALUES = ['0', '.02em', '.06em', '.12em', '.18em', '.22em'] as const;

// ─────────────────────────────────────────────────────────────────────────────
// The registry
// ─────────────────────────────────────────────────────────────────────────────

export const STYLE_RULE_REGISTRY: Record<string, RuleEntry> = {
  // ── Tabs — the pill-vs-underline switch ───────────────────────────────────
  'MuiTabs.variantStyle': {
    spec: {
      kind: 'enum', group: 'Tabs', label: 'Tab style',
      values: ['pill', 'underline', 'contained'],
      help: 'pill = DLS rounded group · underline = conventional MUI indicator · contained = boxed',
    },
    apply: (v, theme) => {
      const ink = theme.palette.text.primary;
      const rule = theme.palette.rule ?? theme.palette.divider;
      const primary = theme.palette.primary.main;

      if (v === 'underline') {
        return {
          MuiTabs: {
            styleOverrides: {
              root: { backgroundColor: 'transparent', borderRadius: 0, padding: 0, minHeight: 44,
                      borderBottom: `1px solid ${theme.palette.divider}`, width: '100%' },
              indicator: { display: 'block', height: 2, backgroundColor: primary },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                borderRadius: 0, border: 0, boxShadow: 'none',
                backgroundColor: 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'transparent', border: 0, boxShadow: 'none',
                  color: primary, fontWeight: 700,
                },
              },
            },
          },
        };
      }

      if (v === 'contained') {
        return {
          MuiTabs: {
            styleOverrides: {
              root: { backgroundColor: 'transparent', borderRadius: 0, padding: 0, minHeight: 44 },
              indicator: { display: 'none' },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
                border: `1px solid transparent`,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${rule}`,
                  borderBottomColor: theme.palette.background.paper,
                  color: primary, fontWeight: 700, boxShadow: 'none',
                },
              },
            },
          },
        };
      }

      // pill — matches the DLS reference: tinted track, DARKER selected pill.
      // The 1px border is the deliberate addition: the DLS relies on a ~7% luminance delta,
      // which does not survive greyscale or forced-colours mode.
      return {
        MuiTabs: {
          styleOverrides: {
            root: {
              backgroundColor: alpha(ink, 0.07), borderRadius: 999, minHeight: 52,
              padding: 4, width: 'fit-content', borderBottom: 0,
            },
            // Same reasoning as the pack's MuiTabs.vertical slot: a pill radius on a vertical
            // container becomes an ellipse, and Tabs-root has overflow:hidden, so labels clip.
            // `vertical` is applied after `root` by Tabs' overridesResolver.
            vertical: { borderRadius: theme.shape.borderRadius, width: '100%' },
            indicator: { display: 'none' },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              borderRadius: 999,
              border: '1px solid transparent',
              '&.Mui-selected': {
                backgroundColor: alpha(ink, 0.16),
                border: `1px solid ${alpha(ink, 0.22)}`,
                boxShadow: `0 1px 2px ${alpha(ink, 0.12)}`,
                color: ink,
                fontWeight: 700,
              },
            },
          },
        },
      };
    },
  },

  'MuiTab.root.textTransform': {
    spec: { kind: 'enum', group: 'Tabs', label: 'Tab label case', values: TRANSFORM_VALUES },
    apply: (v) => ({ MuiTab: { styleOverrides: { root: { textTransform: v as 'none' } } } }),
  },
  'MuiTab.root.fontSize': {
    spec: { kind: 'length', group: 'Tabs', label: 'Tab font size', min: 12, max: 18 },
    apply: (v) => ({ MuiTab: { styleOverrides: { root: { fontSize: Number(v) } } } }),
  },

  // ── Alerts — accent border vs filled ──────────────────────────────────────
  'MuiAlert.variantStyle': {
    spec: {
      kind: 'enum', group: 'Alerts', label: 'Alert style',
      values: ['accentBorder', 'filled', 'outlined'],
      help: 'accentBorder = DLS inline-start rule · filled = Bootstrap-style tinted panel',
    },
    apply: (v, theme) => {
      const severities = ['success', 'warning', 'error', 'info'] as const;
      const rule = theme.palette.rule ?? theme.palette.divider;

      if (v === 'filled') {
        return {
          MuiAlert: {
            styleOverrides: {
              root: { borderInlineStart: 0, border: `1px solid transparent` },
            },
            variants: severities.map((s) => ({
              props: { severity: s, variant: 'standard' as const },
              style: {
                backgroundColor: alpha(theme.palette[s].main, 0.16),
                borderColor: alpha(theme.palette[s].main, 0.3),
                color: theme.palette[s].dark,
              },
            })),
          },
        };
      }

      if (v === 'outlined') {
        return {
          MuiAlert: {
            styleOverrides: { root: { borderInlineStart: 0, backgroundColor: 'transparent' } },
            variants: severities.map((s) => ({
              props: { severity: s, variant: 'standard' as const },
              style: {
                backgroundColor: 'transparent',
                border: `1px solid ${theme.palette[s].main}`,
                color: theme.palette[s].dark,
              },
            })),
          },
        };
      }

      // accentBorder — the DLS reference. Neutral wash for info, tinted for the rest, with a
      // 4px inline-start rule in the severity colour (inline-start so it flips in RTL).
      return {
        MuiAlert: {
          styleOverrides: {
            root: { border: '1px solid transparent', borderInlineStartWidth: 4, borderInlineStartStyle: 'solid' },
          },
          variants: severities.map((s) => ({
            props: { severity: s, variant: 'standard' as const },
            style: {
              backgroundColor: s === 'info'
                ? alpha(theme.palette.text.primary, 0.05)
                : alpha(theme.palette[s].main, 0.1),
              borderInlineStartColor: theme.palette[s].main,
              borderColor: rule,
              color: theme.palette[s].dark,
            },
          })),
        },
      };
    },
  },

  // ── Tables ────────────────────────────────────────────────────────────────
  'MuiTable.variantStyle': {
    spec: {
      kind: 'enum', group: 'Tables', label: 'Table style',
      values: ['mui', 'dls', 'bordered', 'striped'],
      help: 'mui = current portal look · dls = uppercase display headers on a tonal wash',
    },
    apply: (v, theme) => {
      const rule = theme.palette.rule ?? theme.palette.divider;
      const soft = theme.palette.soft ?? alpha(theme.palette.text.primary, 0.04);
      const display = theme.typography.h1?.fontFamily ?? theme.typography.fontFamily;

      if (v === 'dls') {
        return {
          MuiTableCell: {
            styleOverrides: {
              root: { borderBottom: `1px solid ${rule}`, padding: '12px 16px', fontSize: 14 },
              head: {
                backgroundColor: soft, fontFamily: display, fontSize: 12, fontWeight: 700,
                letterSpacing: '.06em', textTransform: 'uppercase',
              },
            },
          },
          MuiTableRow: { styleOverrides: { root: { '&:hover': { backgroundColor: soft } } } },
        };
      }
      if (v === 'bordered') {
        return {
          MuiTableCell: {
            styleOverrides: {
              root: { border: `1px solid ${rule}`, padding: '10px 14px' },
              head: { backgroundColor: soft, fontWeight: 700 },
            },
          },
        };
      }
      if (v === 'striped') {
        return {
          MuiTableRow: {
            styleOverrides: {
              root: { '&:nth-of-type(odd)': { backgroundColor: soft } },
            },
          },
          MuiTableCell: { styleOverrides: { head: { backgroundColor: 'transparent', fontWeight: 700 } } },
        };
      }
      // mui — the portal default: hairline rows, sentence-case headers.
      return {
        MuiTableCell: {
          styleOverrides: {
            root: { border: 0, borderBottom: `1px solid ${theme.palette.divider}` },
            head: { backgroundColor: 'transparent', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
          },
        },
      };
    },
  },
  'MuiTableCell.head.textTransform': {
    spec: { kind: 'enum', group: 'Tables', label: 'Header case', values: TRANSFORM_VALUES },
    apply: (v) => ({ MuiTableCell: { styleOverrides: { head: { textTransform: v as 'none' } } } }),
  },

  // ── Fields ────────────────────────────────────────────────────────────────
  'MuiTextField.fieldStyle': {
    spec: {
      kind: 'enum', group: 'Fields', label: 'Field style',
      values: ['dls', 'mui', 'compact'],
      help: 'dls = 48px with the label above · mui = floating notched label · compact = dense',
    },
    apply: (v, theme) => {
      // Not `rule`: an input's border identifies the control, so SC 1.4.11 wants 3:1. The DLS
      // uses --rule (22% black = 1.69:1) here, and MUI's own default (0.23) fails the same way.
      const border = uiBorderColor(theme);
      const primary = theme.palette.primary.main;

      // Shared focus treatment: the 2px border carries the state, the ring reinforces it.
      const focus = {
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(primary, 0.24)}` },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primary, borderWidth: 2 },
      };

      if (v === 'mui') {
        return {
          MuiInputLabel: {
            defaultProps: { shrink: undefined },
            styleOverrides: {
              root: {
                position: 'absolute',
                transform: undefined,
                color: theme.palette.text.secondary,
                '&.Mui-focused': { color: primary },
                '&.Mui-error': { color: theme.palette.error.main },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                minHeight: 40, borderRadius: theme.shape.borderRadius, ...focus,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: border, top: -5 },
                '& .MuiOutlinedInput-notchedOutline legend': { display: 'table' },
              },
              input: { height: 'auto', padding: '10px 14px' },
            },
          },
        };
      }
      if (v === 'compact') {
        return {
          MuiOutlinedInput: {
            styleOverrides: {
              root: { minHeight: 36, borderRadius: theme.shape.borderRadius, ...focus },
              input: { height: 36, padding: '8px 12px', fontSize: 14 },
            },
          },
        };
      }
      // dls — 48px, 12px radius (selects r-8), static label above the field, notch closed.
      return {
        MuiSelect: { styleOverrides: { root: { borderRadius: 8 } } },
        MuiInputLabel: {
          defaultProps: { shrink: true },
          styleOverrides: {
            root: { position: 'static', transform: 'none', fontSize: 14, fontWeight: 600, margin: 0 },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              minHeight: 48, borderRadius: 12, ...focus,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: border, top: 0 },
              '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            },
            // fontWeight 400, not the DLS's --fw-light 300: an input is where a patient verifies
            // what they typed, and 300 at 15px is thin for that.
            input: {
              height: 48, boxSizing: 'border-box', padding: '14px 16px',
              fontSize: 15, fontWeight: 400,
            },
          },
        },
      };
    },
  },
  'MuiOutlinedInput.root.borderRadius': {
    spec: { kind: 'length', group: 'Fields', label: 'Field radius', min: 0, max: 32 },
    apply: (v) => ({ MuiOutlinedInput: { styleOverrides: { root: { borderRadius: Number(v) } } } }),
  },
  'MuiOutlinedInput.root.minHeight': {
    spec: {
      kind: 'length', group: 'Fields', label: 'Field height', min: 36, max: 64,
      help: 'Floor of 36 keeps the control above the minimum target size',
    },
    apply: (v) => ({
      MuiOutlinedInput: {
        styleOverrides: { root: { minHeight: Number(v) }, input: { height: Number(v) } },
      },
    }),
  },

  // ── Buttons ───────────────────────────────────────────────────────────────
  'MuiButton.root.borderRadius': {
    spec: { kind: 'length', group: 'Buttons', label: 'Button radius', min: 0, max: 999, help: '999 = pill' },
    apply: (v) => ({ MuiButton: { styleOverrides: { root: { borderRadius: Number(v) } } } }),
  },
  'MuiButton.root.textTransform': {
    spec: { kind: 'enum', group: 'Buttons', label: 'Button case', values: TRANSFORM_VALUES },
    apply: (v) => ({ MuiButton: { styleOverrides: { root: { textTransform: v as 'none' } } } }),
  },
  'MuiButton.root.fontWeight': {
    spec: { kind: 'number', group: 'Buttons', label: 'Button weight', values: WEIGHT_VALUES },
    apply: (v) => ({ MuiButton: { styleOverrides: { root: { fontWeight: Number(v) } } } }),
  },
  'MuiButton.root.letterSpacing': {
    spec: { kind: 'enum', group: 'Buttons', label: 'Button tracking', values: TRACKING_VALUES },
    apply: (v) => ({ MuiButton: { styleOverrides: { root: { letterSpacing: v } } } }),
  },
  'MuiButton.root.minHeight': {
    spec: {
      kind: 'length', group: 'Buttons', label: 'Button height', min: 32, max: 64,
      help: 'Floor of 32 protects the 24px minimum target size (WCAG 2.2 SC 2.5.8)',
    },
    apply: (v) => ({ MuiButton: { styleOverrides: { root: { minHeight: Number(v) } } } }),
  },
  'MuiButton.contained.boxShadow': {
    spec: { kind: 'enum', group: 'Buttons', label: 'Contained shadow', values: SHADOW_VALUES },
    apply: (v, theme) => ({
      MuiButton: {
        variants: [{ props: { variant: 'contained' }, style: { boxShadow: shadow(theme, v) } }],
      },
    }),
  },

  // ── Cards & surfaces ──────────────────────────────────────────────────────
  'MuiCard.root.borderRadius': {
    spec: { kind: 'length', group: 'Cards', label: 'Card radius', min: 0, max: 32 },
    apply: (v) => ({ MuiCard: { styleOverrides: { root: { borderRadius: Number(v) } } } }),
  },
  'MuiCard.root.borderWidth': {
    spec: { kind: 'length', group: 'Cards', label: 'Card border', min: 0, max: 2 },
    apply: (v) => ({ MuiCard: { styleOverrides: { root: { borderWidth: Number(v) } } } }),
  },
  'MuiCard.root.boxShadow': {
    spec: { kind: 'enum', group: 'Cards', label: 'Card shadow', values: SHADOW_VALUES },
    apply: (v, theme) => ({ MuiCard: { styleOverrides: { root: { boxShadow: shadow(theme, v) } } } }),
  },
  'MuiPaper.root.borderRadius': {
    spec: { kind: 'length', group: 'Surfaces', label: 'Paper radius', min: 0, max: 32 },
    apply: (v) => ({ MuiPaper: { styleOverrides: { root: { borderRadius: Number(v) } } } }),
  },
  'MuiDialog.paper.borderRadius': {
    spec: { kind: 'length', group: 'Surfaces', label: 'Dialog radius', min: 0, max: 32 },
    apply: (v) => ({ MuiDialog: { styleOverrides: { paper: { borderRadius: Number(v) } } } }),
  },
  'MuiAppBar.root.background': {
    spec: { kind: 'enum', group: 'Surfaces', label: 'App bar', values: ['solid', 'gradient', 'transparent'] },
    apply: (v, theme) => {
      const deep = theme.palette.deep ?? theme.palette.primary.dark;
      if (v === 'gradient') {
        return {
          MuiAppBar: {
            styleOverrides: {
              root: { backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${deep})` },
            },
          },
        };
      }
      if (v === 'transparent') {
        return { MuiAppBar: { styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none' } } } };
      }
      return { MuiAppBar: { styleOverrides: { root: { backgroundImage: 'none' } } } };
    },
  },

  // ── Chips ─────────────────────────────────────────────────────────────────
  'MuiChip.root.borderRadius': {
    spec: { kind: 'length', group: 'Chips', label: 'Chip radius', min: 0, max: 999 },
    apply: (v) => ({ MuiChip: { styleOverrides: { root: { borderRadius: Number(v) } } } }),
  },
  'MuiChip.root.textTransform': {
    spec: { kind: 'enum', group: 'Chips', label: 'Chip case', values: TRANSFORM_VALUES },
    apply: (v) => ({ MuiChip: { styleOverrides: { root: { textTransform: v as 'none' } } } }),
  },
  'MuiChip.root.fontSize': {
    spec: {
      kind: 'length', group: 'Chips', label: 'Chip font size', min: 12, max: 16,
      help: 'Floor of 12 for legibility',
    },
    apply: (v) => ({ MuiChip: { styleOverrides: { root: { fontSize: Number(v) } } } }),
  },

  // ── Disclosure & lists ────────────────────────────────────────────────────
  'MuiAccordion.root.borderRadius': {
    spec: { kind: 'length', group: 'Disclosure', label: 'Accordion radius', min: 0, max: 32 },
    apply: (v) => ({
      MuiAccordion: {
        styleOverrides: {
          root: {
            '&:first-of-type': { borderStartStartRadius: Number(v), borderStartEndRadius: Number(v) },
            '&:last-of-type': { borderEndStartRadius: Number(v), borderEndEndRadius: Number(v) },
          },
        },
      },
    }),
  },
  'MuiAccordionSummary.content.textTransform': {
    spec: { kind: 'enum', group: 'Disclosure', label: 'Summary case', values: TRANSFORM_VALUES },
    apply: (v) => ({
      MuiAccordionSummary: { styleOverrides: { content: { textTransform: v as 'none' } } },
    }),
  },
  'MuiListItemButton.selected.markerWidth': {
    spec: {
      kind: 'length', group: 'Lists', label: 'Selected marker', min: 0, max: 6,
      help: 'Inline-start bar on the selected row; flips automatically in RTL',
    },
    apply: (v, theme) => ({
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              boxShadow: Number(v) > 0
                ? `inset ${Number(v)}px 0 0 ${theme.palette.primary.main}`
                : 'none',
            },
          },
        },
      },
    }),
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Validation + application
// ─────────────────────────────────────────────────────────────────────────────

export interface RuleIssue {
  key: string;
  message: string;
}

/**
 * Semantic validation for the admin editor. The apply path is fail-safe regardless, so this
 * exists to give an author a readable message rather than silent no-ops.
 */
export function validateStyleRules(rules: Record<string, unknown>): RuleIssue[] {
  const issues: RuleIssue[] = [];
  for (const [key, raw] of Object.entries(rules)) {
    const entry = STYLE_RULE_REGISTRY[key];
    if (!entry) {
      issues.push({ key, message: `Not a registered style rule — it will be ignored.` });
      continue;
    }
    const value = String(raw);
    const { spec } = entry;
    if (spec.kind === 'enum' && !spec.values.includes(value)) {
      issues.push({ key, message: `Must be one of: ${spec.values.join(', ')}.` });
    } else if (spec.kind === 'number' && !spec.values.map(String).includes(value)) {
      issues.push({ key, message: `Must be one of: ${spec.values.join(', ')}.` });
    } else if (spec.kind === 'length') {
      const n = Number(value.replace(/(px|rem|em)$/i, '').trim());
      if (!Number.isFinite(n)) issues.push({ key, message: 'Must be a number.' });
      else if (n < spec.min || n > spec.max) {
        issues.push({ key, message: `Must be between ${spec.min} and ${spec.max}.` });
      }
    }
  }
  return issues;
}

/** Deep-merge MUI component fragments. Later fragments win on scalar collisions. */
function mergeFragments(target: Record<string, unknown>, source: Record<string, unknown>): void {
  for (const [k, v] of Object.entries(source)) {
    if (v === undefined) continue;
    const existing = target[k];
    if (Array.isArray(v)) {
      // `variants` arrays concatenate — a later fragment adds cases rather than replacing them.
      target[k] = Array.isArray(existing) ? [...existing, ...v] : [...v];
    } else if (v && typeof v === 'object') {
      if (!existing || typeof existing !== 'object' || Array.isArray(existing)) target[k] = {};
      mergeFragments(target[k] as Record<string, unknown>, v as Record<string, unknown>);
    } else {
      target[k] = v;
    }
  }
}

/**
 * Turn a validated-or-not rule map into a `components` object.
 *
 * Fail-safe: an unregistered key is skipped, an invalid enum is skipped, and a length is CLAMPED
 * into its declared range rather than dropped — so a base authored against a newer registry
 * degrades instead of breaking, and an out-of-range value cannot shrink a control below its floor.
 */
export function applyStyleRules(
  rules: Record<string, unknown> | null | undefined,
  theme: Theme,
): Components<Theme> {
  if (!rules) return {};
  const out: Record<string, unknown> = {};

  for (const [key, raw] of Object.entries(rules)) {
    const entry = STYLE_RULE_REGISTRY[key];
    if (!entry) continue;

    let value = String(raw ?? '').trim();
    if (value === '') continue;

    const { spec } = entry;
    if (spec.kind === 'enum') {
      if (!spec.values.includes(value)) continue;
    } else if (spec.kind === 'number') {
      if (!spec.values.map(String).includes(value)) continue;
    } else {
      // Accept a unit suffix. Authors reasonably write "16px", and Number("16px") is NaN, which
      // would skip the rule silently — indistinguishable from a rule that applied.
      const n = Number(value.replace(/(px|rem|em)$/i, '').trim());
      if (!Number.isFinite(n)) continue;
      value = String(Math.min(spec.max, Math.max(spec.min, n)));
    }

    try {
      mergeFragments(out, entry.apply(value, theme) as Record<string, unknown>);
    } catch {
      // A single bad applier must not cost the whole theme.
    }
  }

  return out as Components<Theme>;
}

/** Registry metadata for tooling. Mirrored by PatientAdminMUI's styleRuleCatalog.ts. */
export const STYLE_RULE_SPECS: Record<string, RuleSpec> = Object.fromEntries(
  Object.entries(STYLE_RULE_REGISTRY).map(([k, e]) => [k, e.spec]),
);
